/**
 * Viator interest taxonomy loader + selection helpers (iOS ViatorInterestTaxonomy parity).
 */

import taxonomyJson from '../data/viator_interest_taxonomy_60.json';
import {
  ACTIVITY_INTEREST_MAX,
  ACTIVITY_INTEREST_MIN,
} from './travelerPreferenceOptions';

export interface ViatorInterest {
  id: string;
  name: string;
  description: string;
}

export interface ViatorInterestGroup {
  id: string;
  name: string;
  description: string;
  children: ViatorInterest[];
}

export interface ViatorInterestTaxonomy {
  schemaVersion: string;
  categoryCount: number;
  groupCount: number;
  groups: ViatorInterestGroup[];
}

let cached: ViatorInterestTaxonomy | null = null;

export function loadViatorInterestTaxonomy(): ViatorInterestTaxonomy {
  if (cached) return cached;
  cached = taxonomyJson as ViatorInterestTaxonomy;
  return cached;
}

export function loadViatorInterestGroups(): ViatorInterestGroup[] {
  return loadViatorInterestTaxonomy().groups;
}

export type ActivityInterestGroupSelectionState =
  | 'none'
  | 'allIncluded'
  | { partial: number };

export function activityInterestSelectionState(
  group: ViatorInterestGroup,
  selection: ReadonlySet<string>
): ActivityInterestGroupSelectionState {
  if (selection.has(group.id)) return 'allIncluded';
  const count = group.children.filter((c) => selection.has(c.id)).length;
  if (count > 0) return { partial: count };
  return 'none';
}

/** All-included group counts as 1; each child counts separately. */
export function effectiveActivityInterestCount(
  selection: ReadonlySet<string>,
  groups: ViatorInterestGroup[] = loadViatorInterestGroups()
): number {
  let count = 0;
  for (const group of groups) {
    if (selection.has(group.id)) {
      count += 1;
    } else {
      count += group.children.filter((c) => selection.has(c.id)).length;
    }
  }
  return count;
}

export function toggleActivityInterestGroup(
  group: ViatorInterestGroup,
  selection: ReadonlySet<string>
): Set<string> {
  const next = new Set(selection);
  if (next.has(group.id)) {
    next.delete(group.id);
    return next;
  }
  const effective = effectiveActivityInterestCount(next);
  if (effective >= ACTIVITY_INTEREST_MAX) return next;
  for (const child of group.children) next.delete(child.id);
  next.add(group.id);
  return next;
}

export function toggleActivityInterestChild(
  interestId: string,
  group: ViatorInterestGroup,
  selection: ReadonlySet<string>
): Set<string> {
  const next = new Set(selection);
  if (next.has(interestId)) {
    next.delete(interestId);
    return next;
  }
  if (next.has(group.id)) {
    next.delete(group.id);
    for (const child of group.children) {
      if (child.id !== interestId) next.add(child.id);
    }
    return next;
  }
  const effective = effectiveActivityInterestCount(next);
  if (effective >= ACTIVITY_INTEREST_MAX) return next;
  next.add(interestId);
  return next;
}

const LEGACY_TO_TAXONOMY: Record<string, string> = {
  food: 'food_tours',
  shopping: 'shopping_tours',
  nightlife: 'nightlife_bars',
  adventure: 'hiking_running',
  relaxation: 'spas_baths',
  history: 'ancient_civilizations',
  art: 'architecture_landmarks',
};

const PLAN_API_PAD_DEFAULTS = [
  'architecture_landmarks',
  'food_tours',
  'national_parks_natural_attractions',
];

function taxonomyIndex(): {
  groupsById: Map<string, ViatorInterestGroup>;
  interestsById: Map<string, ViatorInterest>;
} {
  const groupsById = new Map<string, ViatorInterestGroup>();
  const interestsById = new Map<string, ViatorInterest>();
  for (const group of loadViatorInterestGroups()) {
    groupsById.set(group.id, group);
    for (const child of group.children) {
      interestsById.set(child.id, child);
    }
  }
  return { groupsById, interestsById };
}

function displayNameForSelectionId(id: string): string {
  const { groupsById, interestsById } = taxonomyIndex();
  return groupsById.get(id)?.name ?? interestsById.get(id)?.name ?? id.replace(/_/g, ' ');
}

/** Localized-style names for the POST /plan text blob (not structured API ids). */
export function formattedActivityInterestNames(ids: string[]): string {
  return ids.map((id) => displayNameForSelectionId(id)).join(', ');
}

function appendUnique(id: string, result: string[], limit: number): void {
  if (result.length >= limit || result.includes(id)) return;
  result.push(id);
}

function padToMinimum(ids: string[]): string[] {
  const unique = Array.from(new Set(ids));
  if (unique.length >= ACTIVITY_INTEREST_MIN) return unique;
  for (const pad of PLAN_API_PAD_DEFAULTS) {
    if (!unique.includes(pad)) unique.push(pad);
    if (unique.length >= ACTIVITY_INTEREST_MIN) break;
  }
  return unique;
}

/**
 * Leaf Viator interest ids for POST /plan — mirrors iOS
 * LegacyActivityInterestMigration.planAPIInterestIds.
 */
export function planAPIInterestIds(storedIds: string[]): string[] {
  const trimmed = storedIds.map((id) => id.trim()).filter(Boolean);
  if (trimmed.length === 0) {
    return padToMinimum([]).sort();
  }

  const { groupsById, interestsById } = taxonomyIndex();
  const selection = new Set(trimmed);
  const effectiveCount = Math.max(1, effectiveActivityInterestCount(selection));
  const perItemBudget = Math.max(1, Math.floor(ACTIVITY_INTEREST_MAX / effectiveCount));
  const result: string[] = [];

  for (const raw of trimmed) {
    if (result.length >= ACTIVITY_INTEREST_MAX) break;
    const mapped = LEGACY_TO_TAXONOMY[raw.toLowerCase()];
    if (mapped) {
      appendUnique(mapped, result, ACTIVITY_INTEREST_MAX);
      continue;
    }
    if (interestsById.has(raw)) {
      appendUnique(raw, result, ACTIVITY_INTEREST_MAX);
      continue;
    }
    const group = groupsById.get(raw);
    if (!group) continue;
    let added = 0;
    for (const child of group.children) {
      if (result.length >= ACTIVITY_INTEREST_MAX || added >= perItemBudget) break;
      appendUnique(child.id, result, ACTIVITY_INTEREST_MAX);
      added += 1;
    }
  }

  if (result.length === 0) {
    return padToMinimum([]).sort();
  }
  return [...result].sort();
}

export { ACTIVITY_INTEREST_MIN, ACTIVITY_INTEREST_MAX };
